/**
 * Workshop master layout — self-contained workspace shell with mock data.
 *
 * Mirrors production MasterLayout but uses WorkshopLayoutProvider instead
 * of stores/hooks/websocket. All data is hardcoded.
 */

import { useState, useCallback, useRef, useEffect } from "react";
import type { ReactNode } from "react";
import { cn } from "@/lib/cn";
import { Icons } from "@/components/ui/icons";
import { useWorkshopLayout } from "@/workshop/components/layout/layout-provider";
import { AppHeader } from "@/workshop/components/panels/app-header";
import { LeftSidebar } from "@/workshop/components/panels/left-sidebar";
import { RightPanel } from "@/workshop/components/panels/right-panel";
import { FloatingToolbar } from "@/workshop/components/canvas/floating-toolbar";
import { CanvasTransformContext } from "@/workshop/components/canvas/canvas-viewport";
import { InputBar } from "@/workshop/components/panels/input-bar";
import type {
  FrameEntry,
  TreeEntry,
  TaskEntry,
} from "@/workshop/components/panels/left-sidebar";
import type { DetailSection } from "@/workshop/components/panels/right-panel";
import type { WorkflowStep } from "@/workshop/components/panels/execution-sidebar";

/* ---------- Mock data ---------- */

const MOCK_PROJECT_NAME = "Marketing Website Redesign";
const MOCK_SPACE_NAME = "Research";
const MOCK_USER_LETTER = "A";

const MOCK_SIDEBAR_FRAMES: FrameEntry[] = [
  {
    id: "f1",
    title: "Agent Chat",
    icon: Icons.MessageSquare,
    active: true,
    statusColor: "bg-accent",
  },
  { id: "f2", title: "api-handler.ts", icon: Icons.Code },
  { id: "f3", title: "Architecture Notes", icon: Icons.FileText },
  { id: "f4", title: "Web Search", icon: Icons.Search },
  { id: "f5", title: "Preview", icon: Icons.Image },
];

const MOCK_TREE_ITEMS: TreeEntry[] = [
  {
    id: "p1",
    label: "Marketing Website Redesign",
    icon: Icons.FolderTree,
    expanded: true,
    depth: 0,
  },
  { id: "s1", label: "Research", icon: Icons.Layers, depth: 1 },
  { id: "s2", label: "Design", icon: Icons.Layers, depth: 1 },
  { id: "s3", label: "Development", icon: Icons.Layers, depth: 1 },
  { id: "s4", label: "Launch", icon: Icons.Layers, depth: 1 },
];

const MOCK_TASKS: TaskEntry[] = [
  {
    id: "t1",
    title: "Refactor auth module",
    checked: false,
    agentLetter: "C",
    agentColor: "bg-accent",
  },
  { id: "t2", title: "Update API docs", checked: true },
  {
    id: "t3",
    title: "Review PR #42",
    checked: false,
    agentLetter: "R",
    agentColor: "bg-blue-400",
  },
];

const MOCK_SPACES = [
  { id: "s1", label: "Research", active: true },
  { id: "s2", label: "Design", active: false },
  { id: "s3", label: "Development", active: false },
  { id: "s4", label: "Launch", active: false },
];

const INITIAL_AGENTS = [
  {
    id: "a1",
    letter: "C",
    name: "Coder",
    statusColor: "bg-accent",
    color: "#30D158",
  },
  {
    id: "a2",
    letter: "R",
    name: "Researcher",
    statusColor: "bg-blue-400",
    color: "#60A5FA",
  },
];

const EXTRA_AGENTS = [
  {
    id: "a3",
    letter: "D",
    name: "Designer",
    statusColor: "bg-purple-400",
    color: "#A78BFA",
  },
  {
    id: "a4",
    letter: "W",
    name: "Writer",
    statusColor: "bg-amber-400",
    color: "#FBBF24",
  },
  {
    id: "a5",
    letter: "T",
    name: "Tester",
    statusColor: "bg-rose-400",
    color: "#FB7185",
  },
];

const MOCK_EXEC_STEPS: WorkflowStep[] = [
  { index: 1, label: "Analyze failing tests", status: "completed" },
  { index: 2, label: "Run test suite", status: "completed" },
  { index: 3, label: "Fix auth middleware", status: "running" },
  { index: 4, label: "Verify fix passes", status: "pending" },
  { index: 5, label: "Update snapshots", status: "pending" },
];

const MOCK_DETAIL_SECTIONS: DetailSection[] = [
  {
    title: "Frame Info",
    rows: [
      { label: "Type", value: "chat" },
      { label: "Status", value: "streaming" },
      { label: "ID", value: "f1a3b2e1" },
    ],
  },
  {
    title: "Agent",
    rows: [{ label: "Agent ID", value: "claude-main" }],
  },
];

/* ---------- Component ---------- */

interface WorkshopMasterLayoutProps {
  children?: ReactNode;
  /** Hide canvas overlays (toolbar, input bar, agent dock) */
  hideOverlays?: boolean;
  /** Called when the user avatar is clicked (opens settings) */
  onUserMenu?: () => void;
  /** Seeds the input bar with this value on mount (for demo walkthrough). */
  defaultInputValue?: string;
  /** If provided, called instead of the default console.log submit handler. */
  onInputSubmit?: () => void;
  /** When true, the input bar is rendered in a disabled state. */
  disabledInput?: boolean;
  /** When provided, renders in place of the default RightPanel. */
  rightPanelContent?: ReactNode;
}

export function WorkshopMasterLayout({
  children,
  hideOverlays = false,
  onUserMenu,
  defaultInputValue,
  onInputSubmit,
  disabledInput,
  rightPanelContent,
}: WorkshopMasterLayoutProps) {
  const layout = useWorkshopLayout();

  const [zoomLevel, setZoomLevel] = useState(100);
  const [panX, setPanX] = useState(0);
  const [panY, setPanY] = useState(0);
  const [isPanMode, setIsPanMode] = useState(false);
  const [inputValue, setInputValue] = useState(defaultInputValue ?? "");
  const [selectedFrameId, setSelectedFrameId] = useState<string | null>("f1");
  const [agents, setAgents] = useState(INITIAL_AGENTS);
  const [selectedAgentId, setSelectedAgentId] = useState<string | null>(null);
  const [nextAgentIdx, setNextAgentIdx] = useState(0);

  // Refs for stable access inside event handlers (avoids stale closures)
  const zoomRef = useRef(1);
  const panXRef = useRef(0);
  const panYRef = useRef(0);
  const isPanModeRef = useRef(false);
  const panDragRef = useRef<{ sx: number; sy: number; px: number; py: number } | null>(null);
  const canvasAreaRef = useRef<HTMLDivElement>(null);

  // Keep refs in sync with state so event handlers always read fresh values
  useEffect(() => {
    zoomRef.current = zoomLevel / 100;
    panXRef.current = panX;
    panYRef.current = panY;
    isPanModeRef.current = isPanMode;
  }, [zoomLevel, panX, panY, isPanMode]);

  const handleZoomIn = useCallback(() => {
    const oldZoom = zoomRef.current;
    const newZoom = Math.min(oldZoom + 0.1, 2.0);
    const cx = window.innerWidth / 2;
    const cy = window.innerHeight / 2;
    const r = newZoom / oldZoom;
    setPanX(panXRef.current * r + cx * (1 - r));
    setPanY(panYRef.current * r + cy * (1 - r));
    setZoomLevel(newZoom * 100);
  }, []);

  const handleZoomOut = useCallback(() => {
    const oldZoom = zoomRef.current;
    const newZoom = Math.max(oldZoom - 0.1, 0.25);
    const cx = window.innerWidth / 2;
    const cy = window.innerHeight / 2;
    const r = newZoom / oldZoom;
    setPanX(panXRef.current * r + cx * (1 - r));
    setPanY(panYRef.current * r + cy * (1 - r));
    setZoomLevel(newZoom * 100);
  }, []);

  const handlePanToggle = useCallback(() => {
    setIsPanMode((p) => !p);
  }, []);

  const handleCanvasMouseDown = useCallback((e: React.MouseEvent) => {
    if (!isPanModeRef.current) return;
    e.preventDefault();
    panDragRef.current = { sx: e.clientX, sy: e.clientY, px: panXRef.current, py: panYRef.current };
  }, []);

  // Wheel zoom + pan drag via native listeners (passive: false required for wheel)
  useEffect(() => {
    const el = canvasAreaRef.current;
    if (!el) return;

    const handleWheel = (e: WheelEvent) => {
      e.preventDefault();
      const oldZoom = zoomRef.current;
      const factor = Math.pow(0.999, e.deltaY);
      const newZoom = Math.min(Math.max(oldZoom * factor, 0.25), 2.0);
      const r = newZoom / oldZoom;
      const newPanX = panXRef.current * r + e.clientX * (1 - r);
      const newPanY = panYRef.current * r + e.clientY * (1 - r);
      setZoomLevel(newZoom * 100);
      setPanX(newPanX);
      setPanY(newPanY);
    };

    const handleMouseMove = (e: MouseEvent) => {
      if (!panDragRef.current) return;
      const { sx, sy, px, py } = panDragRef.current;
      setPanX(px + e.clientX - sx);
      setPanY(py + e.clientY - sy);
    };

    const handleMouseUp = () => { panDragRef.current = null; };

    el.addEventListener("wheel", handleWheel, { passive: false });
    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);

    return () => {
      el.removeEventListener("wheel", handleWheel);
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, []);

  const handleAddAgent = useCallback(() => {
    const newAgent = EXTRA_AGENTS[nextAgentIdx];
    if (!newAgent) {
      console.log("[workshop] No more agents to add");
      return;
    }
    setAgents((prev) => [...prev, newAgent]);
    setSelectedAgentId(newAgent.id);
    setNextAgentIdx((i) => i + 1);
  }, [nextAgentIdx]);

  const handleInputSubmit = useCallback(() => {
    if (!inputValue.trim()) return;
    if (onInputSubmit) {
      onInputSubmit();
    } else {
      console.log("[workshop] Prompt submitted:", inputValue.trim());
    }
    setInputValue("");
  }, [inputValue, onInputSubmit]);

  const handleSelectFrame = useCallback(
    (id: string) => {
      setSelectedFrameId(id);
      layout.setRightPanelOpen(true);
    },
    [layout],
  );

  const framesWithSelection = MOCK_SIDEBAR_FRAMES.map((f) => ({
    ...f,
    active: f.id === selectedFrameId,
  }));

  return (
    <div
      className="relative h-screen bg-canvas"
      data-testid="workshop-master-layout"
    >
      {/* Canvas area — full screen behind everything */}
      <CanvasTransformContext.Provider value={{ zoom: zoomLevel / 100, panX, panY, isPanMode }}>
        <div
          ref={canvasAreaRef}
          className="absolute inset-0"
          style={{ cursor: isPanMode ? "grab" : undefined }}
          onMouseDown={handleCanvasMouseDown}
          data-testid="canvas-area"
        >
          <div
            style={{
              transform: `translate(${panX}px, ${panY}px) scale(${zoomLevel / 100})`,
              transformOrigin: "0 0",
              position: "absolute",
              inset: 0,
            }}
          >
            {children}
          </div>
        </div>
      </CanvasTransformContext.Provider>

      {/* Header — floating, z-20 */}
      <div className="pointer-events-none absolute inset-x-0 top-0 z-20">
        <AppHeader
          onToggleSidebar={layout.toggleSidebar}
          onToggleRightPanel={layout.toggleRightPanel}
          projectName={MOCK_PROJECT_NAME}
          spaceName={MOCK_SPACE_NAME}
          spaces={MOCK_SPACES}
          onSelectSpace={(id) => console.log("[workshop] Space selected:", id)}
          onAddSpace={() => console.log("[workshop] Add space")}
          userLetter={MOCK_USER_LETTER}
          notificationCount={3}
          onSearch={() => console.log("[workshop] Search clicked")}
          onNotifications={() => console.log("[workshop] Notifications clicked")}
          onUserMenu={
            onUserMenu ?? (() => console.log("[workshop] User menu clicked"))
          }
        />
      </div>

      {/* Left sidebar — floating glass card, z-10 */}
      <div
        className={cn(
          "pointer-events-none absolute bottom-3 top-14 z-10 transition-all duration-200 ease-out",
          layout.sidebarOpen
            ? "pointer-events-auto left-3 opacity-100"
            : "pointer-events-none -left-64 opacity-0",
        )}
      >
        <LeftSidebar
          activeTab={layout.sidebarTab}
          frames={framesWithSelection}
          treeItems={MOCK_TREE_ITEMS}
          tasks={MOCK_TASKS}
          onTabChange={layout.setSidebarTab}
          onAddFrame={() => console.log("[workshop] Add frame")}
          onSelectFrame={handleSelectFrame}
          onSelectTreeItem={(id) =>
            console.log("[workshop] Tree item selected:", id)
          }
          onToggleTreeItem={(id) =>
            console.log("[workshop] Tree item toggled:", id)
          }
          onSelectTask={(id) => console.log("[workshop] Task selected:", id)}
          onToggleTask={(id) => console.log("[workshop] Task toggled:", id)}
          execution={{
            executionState: "running",
            todos: [],
            workflowSteps: MOCK_EXEC_STEPS,
            workflowName: "Fix Failing CI",
            onStop: () => console.log("[workshop] Stop execution"),
          }}
          className="h-full"
        />
      </div>

      {/* Right panel — floating glass card, z-10 */}
      <div
        className={cn(
          "pointer-events-none absolute bottom-3 top-14 z-10 transition-all duration-200 ease-out",
          layout.rightPanelOpen
            ? "pointer-events-auto right-3 opacity-100"
            : "pointer-events-none -right-64 opacity-0",
        )}
      >
        {rightPanelContent ?? (
          <RightPanel
            activeTab={layout.rightPanelTab}
            sections={MOCK_DETAIL_SECTIONS}
            onTabChange={layout.setRightPanelTab}
            onClose={layout.toggleRightPanel}
            onEdit={() => console.log("[workshop] Edit frame")}
            onDelete={() => console.log("[workshop] Delete frame")}
            className="h-full"
          />
        )}
      </div>

      {/* Canvas overlays — z-30, shift when panels are open */}
      {!hideOverlays && (
        <>
          {/* Right-side: floating toolbar — shifts left when right panel open */}
          <div
            className="pointer-events-auto absolute bottom-4 right-4 z-30 transition-all duration-200 ease-out"
            style={{ right: layout.rightPanelOpen ? 264 : 16 }}
            data-testid="overlay-floating-toolbar"
          >
            <FloatingToolbar
              zoomLevel={zoomLevel}
              isPanMode={isPanMode}
              onZoomIn={handleZoomIn}
              onZoomOut={handleZoomOut}
              onPan={handlePanToggle}
              onComment={() => console.log("[workshop] Comment")}
              onNewFrame={() => console.log("[workshop] New frame")}
            />
          </div>

          {/* Center: unified input bar with agent stack */}
          <div
            className="pointer-events-auto absolute bottom-4 left-1/2 z-30 w-full max-w-xl -translate-x-1/2 px-4"
            data-testid="overlay-input-bar"
          >
            <InputBar
              value={inputValue}
              onChange={setInputValue}
              onSubmit={handleInputSubmit}
              agents={agents.map((a) => ({
                id: a.id,
                name: a.name,
                color: a.color,
              }))}
              onAddAgent={handleAddAgent}
              selectedAgentId={selectedAgentId}
              onSelectAgent={setSelectedAgentId}
              disabled={disabledInput}
            />
          </div>

        </>
      )}
    </div>
  );
}
